/**
 * StudentDetail — the "full picture" for one student.
 * Header stats + 14-day activity + grammar mistakes grouped into
 * heuristic weak-point buckets + privacy-gated conversation transcript.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiAlertCircle, FiMessageSquare, FiTrendingUp, FiLock,
  FiSend, FiBell, FiX, FiCheck,
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import teacherAPI from '../services/api';

/**
 * V1 heuristic: bucket a grammar-feedback string into a weak-point area
 * by keyword. Clearly an approximation; V2 replaces with a server-side
 * taxonomy. Spanish + English keywords because Carlos may explain in either.
 */
const BUCKETS = [
  { key: 'ser-estar', label: 'Ser vs. Estar', kw: ['ser', 'estar', 'tener calor', 'tener hambre', 'tengo calor'] },
  { key: 'subjuntivo', label: 'Subjunctive', kw: ['subjuntiv', 'subjunctive', 'ojalá', 'que + '] },
  { key: 'tiempos', label: 'Verb Tenses', kw: ['pretérit', 'imperfect', 'preterite', 'pasado', 'futuro', 'condicional', 'gerundio', 'participio', 'conjugat', 'conjuga'] },
  { key: 'genero', label: 'Gender & Agreement', kw: ['género', 'gender', 'concordancia', 'agreement', 'masculin', 'femenin', 'el/la', 'artículo', 'article'] },
  { key: 'preposiciones', label: 'Prepositions', kw: ['preposici', 'preposition', 'por/para', 'por ', 'para ', ' a ', ' de ', ' en '] },
  { key: 'pronombres', label: 'Pronouns', kw: ['pronombr', 'pronoun', 'le/lo', 'se ', 'reflexiv', 'clitic'] },
  { key: 'vocabulario', label: 'Vocabulary / Word Choice', kw: ['vocabular', 'word choice', 'palabra', 'significa', 'context', 'ambigu'] },
  { key: 'orden', label: 'Word Order / Syntax', kw: ['orden', 'word order', 'sintax', 'syntax', 'estructura', 'structure'] },
];

function bucketize(grammarHistory) {
  const counts = {};
  const samples = {};
  for (const g of grammarHistory) {
    const text = (g.feedback || '').toLowerCase();
    let matched = false;
    for (const b of BUCKETS) {
      if (b.kw.some((k) => text.includes(k))) {
        counts[b.key] = (counts[b.key] || 0) + 1;
        if (!samples[b.key]) samples[b.key] = g.feedback;
        matched = true;
        break;
      }
    }
    if (!matched) {
      counts.other = (counts.other || 0) + 1;
      if (!samples.other) samples.other = g.feedback;
    }
  }
  const rows = BUCKETS
    .map((b) => ({ ...b, count: counts[b.key] || 0, sample: samples[b.key] }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  if (counts.other) {
    rows.push({ key: 'other', label: 'Other / General', count: counts.other, sample: samples.other });
  }
  return rows;
}

/**
 * Extract the most-frequent quoted phrases from Carlos's corrections.
 * Carlos's feedback frequently quotes the student's error or the correction
 * (e.g. `"estoy caliente"`, `"tengo calor"`). Counting these gives a concrete
 * pedagogical signal: "Top words/phrases this student keeps tripping over."
 */
function extractStrugglingPhrases(grammarHistory, max = 10) {
  if (!Array.isArray(grammarHistory) || grammarHistory.length === 0) return [];
  // Match straight + curly double AND single quotes used by Carlos's feedback
  // (e.g. "estoy caliente", 'estoy caliente', "tengo calor", "Hablas inglés?")
  const QUOTE_RE = /["“'‘`]([^"”'’`]{2,80})["”'’`]/g;
  // Filter words: common English/Spanish stop-words and punctuation-only matches
  const STOP = new Set(['the', 'a', 'an', 'is', 'and', 'or', 'of', 'to', 'in', 'on',
    'i', 'you', 'he', 'she', 'it', 'we', 'they', 'no', 'si', 'sí', 'que', 'es',
    'la', 'el', 'un', 'una', 'y', 'o', 'de', 'en', 'a', 'por', 'para', 'con']);

  const counts = new Map();
  for (const g of grammarHistory) {
    const text = String(g.feedback || '');
    let m;
    QUOTE_RE.lastIndex = 0;
    while ((m = QUOTE_RE.exec(text)) !== null) {
      const raw = m[1].trim();
      if (!raw) continue;
      const lower = raw.toLowerCase();
      const words = lower.split(/\s+/);
      if (words.length > 7) continue;          // skip long sentences
      if (lower.length < 2) continue;
      if (words.length === 1 && STOP.has(lower)) continue;
      // strip surrounding punctuation
      const clean = raw.replace(/^[¿¡.,;:!?\s-]+|[¿¡.,;:!?\s-]+$/g, '');
      if (!clean) continue;
      const key = clean.toLowerCase();
      counts.set(key, { phrase: clean, count: (counts.get(key)?.count || 0) + 1 });
    }
  }
  return Array.from(counts.values())
    .filter((p) => p.count >= 1)
    .sort((a, b) => b.count - a.count)
    .slice(0, max);
}

const NOTIFY_TEMPLATES = [
  {
    id: 'nudge',
    label: '👋 Friendly nudge',
    title: '¿Listo para practicar?',
    message: '¡Hola! Carlos te está esperando para una conversación rápida. Solo 5 minutos al día hacen una gran diferencia. 💪',
    type: 'educational',
  },
  {
    id: 'praise',
    label: '⭐ Great work',
    title: '¡Excelente progreso!',
    message: '¡Tu profesor ha notado tu esfuerzo esta semana! Sigue así — cada conversación te acerca más a la fluidez. 🎉',
    type: 'achievement',
  },
  {
    id: 'review',
    label: '🎯 Review reminder',
    title: 'Hora de repasar',
    message: 'Tu profesor recomienda repasar tu última lección con Carlos. Una conversación corta te ayudará a consolidar lo aprendido.',
    type: 'educational',
  },
  {
    id: 'custom',
    label: '✍️ Write your own',
    title: '',
    message: '',
    type: 'personal',
  },
];

function NotifyModal({ student, onClose, onSent }) {
  const [tplId, setTplId] = useState('nudge');
  const [title, setTitle] = useState(NOTIFY_TEMPLATES[0].title);
  const [message, setMessage] = useState(NOTIFY_TEMPLATES[0].message);
  const [type, setType] = useState(NOTIFY_TEMPLATES[0].type);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const pickTemplate = (id) => {
    setTplId(id);
    const t = NOTIFY_TEMPLATES.find((x) => x.id === id);
    if (t) {
      setTitle(t.title);
      setMessage(t.message);
      setType(t.type);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await teacherAPI.notifyStudent(student.uid, { title: title.trim(), message: message.trim(), type });
      onSent();
    } catch (err) {
      setError(err.message || 'Failed to send');
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20, backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 540, background: '#0a0a0c',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16,
          padding: 28, maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiBell style={{ color: '#2997ff' }} /> Notify {student.displayName}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.6, fontSize: 22 }}>
            <FiX />
          </button>
        </div>
        <p style={{ opacity: 0.5, fontSize: 13, marginBottom: 18 }}>
          Sent as a push notification inside the Hablamos app.
        </p>

        {/* Template chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          {NOTIFY_TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => pickTemplate(t.id)}
              style={{
                padding: '8px 14px', borderRadius: 100, cursor: 'pointer',
                fontSize: 13, fontWeight: 500,
                border: `1px solid ${tplId === t.id ? '#2997ff' : 'rgba(255,255,255,0.12)'}`,
                background: tplId === t.id ? 'rgba(41,151,255,0.15)' : 'rgba(255,255,255,0.03)',
                color: tplId === t.id ? '#2997ff' : 'rgba(245,245,247,0.8)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Title <span style={{ opacity: 0.4 }}>({title.length}/80)</span></label>
          <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))} maxLength={80} placeholder="Short, attention-grabbing" />
        </div>
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Message <span style={{ opacity: 0.4 }}>({message.length}/500)</span></label>
          <textarea
            className="form-input"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 500))}
            rows={4}
            maxLength={500}
            placeholder="Encourage them, remind them, or check in"
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && (
          <div style={{ padding: 12, borderRadius: 8, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#ff9999', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending || !title.trim() || !message.trim()}>
            {sending ? 'Sending…' : (<><FiSend style={{ marginRight: 6, verticalAlign: 'middle' }} /> Send notification</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StudentDetail({ setPageTitle }) {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [student, setStudent] = useState(null);
  const [tab, setTab] = useState('progress');
  const [conv, setConv] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySent, setNotifySent] = useState(null); // {to, ts} on success

  useEffect(() => { setPageTitle('Student Detail'); }, [setPageTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await teacherAPI.getStudent(uid);
      setStudent(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const loadConversations = useCallback(async () => {
    if (conv) return;
    setConvLoading(true);
    try {
      const data = await teacherAPI.getStudentConversations(uid, 30);
      setConv(data);
    } catch (err) {
      setConv({ error: err.message, messages: [] });
    }
    setConvLoading(false);
  }, [uid, conv]);

  useEffect(() => {
    if (tab === 'conversations') loadConversations();
  }, [tab, loadConversations]);

  if (loading) {
    return <div className="loading" style={{ minHeight: '60vh' }}><div className="loading-spinner" /></div>;
  }

  if (error) {
    return (
      <div>
        <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ marginBottom: 16 }}>
          <FiArrowLeft /> Back to Students
        </button>
        <div className="empty-state">
          <div className="empty-state-icon"><FiAlertCircle /></div>
          <div className="empty-state-title">Could not load student</div>
          <div className="empty-state-text">{error}</div>
        </div>
      </div>
    );
  }

  // Prefer the richer server-side analysis; fall back to the client heuristic
  // if an older backend response doesn't include it.
  const serverCats = student.analysis?.categories;
  const weakPoints = (serverCats && serverCats.length >= 0)
    ? serverCats.map((c) => ({
        key: c.key, label: c.label, count: c.count, cefr: c.cefr,
        severity: c.severity, sample: c.examples?.[0], lessonHint: c.lessonHint,
      }))
    : bucketize(student.grammarHistory || []);
  const strugglingPhrases = student.analysis?.strugglingPhrases
    || extractStrugglingPhrases(student.grammarHistory || []);
  const sparkData = (student.activity || []).map((a) => ({
    date: fmtDate(a.date),
    messages: a.messageCount || 0,
  }));
  const maxMsg = Math.max(1, ...sparkData.map((d) => d.messages));
  const maxBucket = Math.max(1, ...weakPoints.map((w) => w.count));

  const SEV_COLOR = { high: '#ff6b6b', medium: '#ffb340', low: '#2997ff' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back to Students
        </button>
        <button className="btn btn-primary" onClick={() => setNotifyOpen(true)}>
          <FiBell style={{ marginRight: 6, verticalAlign: 'middle' }} /> Notify Student
        </button>
      </div>

      {/* Success toast (after sending notification) */}
      {notifySent && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: 'rgba(48,209,88,0.1)', border: '1px solid rgba(48,209,88,0.3)',
          color: '#7fe09e', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14,
        }}>
          <FiCheck /> Notification sent to {notifySent.to}. They'll see it in the Hablamos app.
          <button onClick={() => setNotifySent(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#7fe09e', cursor: 'pointer' }}>
            <FiX />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26 }}>{student.displayName}</h2>
            <div style={{ opacity: 0.5, fontSize: 14 }}>{student.email}</div>
          </div>
          <div style={{ display: 'flex', gap: 24, textAlign: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#2997ff' }}>Lvl {student.level}</div>
              <div style={{ fontSize: 12, opacity: 0.5 }}>{student.xp} XP</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{student.streak || 0}🔥</div>
              <div style={{ fontSize: 12, opacity: 0.5 }}>day streak</div>
            </div>
            <div>
              <div style={{
                fontSize: 28, fontWeight: 700,
                color: student.accuracy === null ? undefined
                  : student.accuracy >= 70 ? '#30d158'
                  : student.accuracy >= 50 ? '#ffb340' : '#ff6b6b',
              }}>
                {student.accuracy === null ? '—' : `${student.accuracy}%`}
              </div>
              <div style={{ fontSize: 12, opacity: 0.5 }}>accuracy</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{student.vocabularyCount}</div>
              <div style={{ fontSize: 12, opacity: 0.5 }}>words</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {[
          { k: 'progress', label: 'Progress & Mistakes', icon: FiTrendingUp },
          { k: 'conversations', label: 'Conversations', icon: FiMessageSquare },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t.k ? '#2997ff' : 'rgba(245,245,247,0.6)',
              padding: '12px 18px', fontSize: 14, fontWeight: 600,
              borderBottom: tab === t.k ? '2px solid #2997ff' : '2px solid transparent',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <t.icon /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'progress' && (
        <>
          {/* 14-day activity */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">Activity — last 14 days</div>
            <div style={{ height: 160, marginTop: 12 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sparkData}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgba(245,245,247,0.4)' }} interval={1} />
                  <Tooltip
                    contentStyle={{ background: '#0a0a0c', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                    labelStyle={{ color: '#f5f5f7' }}
                  />
                  <Bar dataKey="messages" radius={[4, 4, 0, 0]}>
                    {sparkData.map((d, i) => (
                      <Cell key={i} fill={d.messages === 0 ? 'rgba(255,255,255,0.08)' : d.messages >= maxMsg * 0.6 ? '#2997ff' : '#1d6fc0'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ fontSize: 12, opacity: 0.4, marginTop: 8 }}>
              Messages exchanged per day · {student.totalSentences} total sentences ·{' '}
              {student.conversationsCount} conversations
            </div>
          </div>

          {/* Weak points — top struggling topics, severity-ranked */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">Top Struggling Topics</div>
            <div className="card-subtitle" style={{ opacity: 0.5, fontSize: 13, marginBottom: 16 }}>
              Ranked by severity (frequency × recency) from Carlos's last {student.grammarHistory?.length || 0} corrections.
            </div>
            {weakPoints.length === 0 ? (
              <div style={{ opacity: 0.5, padding: '12px 0' }}>
                No corrections recorded yet — the student hasn't made trackable mistakes.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {weakPoints.map((w) => {
                  const pct = Math.round((w.count / maxBucket) * 100);
                  // Severity drives color; fall back to count thresholds for old heuristic
                  const sev = w.severity
                    || (w.count >= 5 ? 'high' : w.count >= 3 ? 'medium' : 'low');
                  const sevColor = SEV_COLOR[sev];
                  return (
                    <div key={w.key} style={{
                      padding: '14px 16px', background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{w.label}</span>
                          {w.cefr && w.cefr !== '—' && (
                            <span className="badge badge-info" style={{ flexShrink: 0 }}>{w.cefr}</span>
                          )}
                        </div>
                        <span style={{
                          flexShrink: 0, fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.04em', padding: '3px 9px', borderRadius: 100,
                          color: sevColor, background: `${sevColor}22`, border: `1px solid ${sevColor}55`,
                        }}>
                          {sev} · {w.count}
                        </span>
                      </div>
                      <div style={{
                        height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', marginBottom: 10,
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', borderRadius: 4, background: sevColor,
                        }} />
                      </div>
                      {w.lessonHint && (
                        <div style={{
                          fontSize: 13, color: '#7ec0ff', marginBottom: w.sample ? 6 : 0,
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                        }}>
                          <span>💡</span><span>{w.lessonHint}</span>
                        </div>
                      )}
                      {w.sample && (
                        <div style={{ fontSize: 12, opacity: 0.55, fontStyle: 'italic' }}>
                          e.g. "{w.sample.slice(0, 130)}{w.sample.length > 130 ? '…' : ''}"
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top struggling words/phrases */}
          {strugglingPhrases.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title">Top Struggling Words &amp; Phrases</div>
              <div className="card-subtitle" style={{ opacity: 0.5, fontSize: 13, marginBottom: 14 }}>
                Phrases Carlos quoted most in corrections — concrete vocabulary to drill in class.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {strugglingPhrases.map((p) => (
                  <span
                    key={p.phrase}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '8px 14px', borderRadius: 100,
                      background: p.count >= 3 ? 'rgba(255,107,107,0.1)' : 'rgba(41,151,255,0.1)',
                      border: `1px solid ${p.count >= 3 ? 'rgba(255,107,107,0.3)' : 'rgba(41,151,255,0.3)'}`,
                      fontSize: 14, fontWeight: 500,
                      color: p.count >= 3 ? '#ff9999' : '#7ec0ff',
                    }}
                  >
                    "{p.phrase}"
                    <span style={{
                      fontSize: 11, opacity: 0.8, fontWeight: 700,
                      padding: '2px 7px', borderRadius: 100,
                      background: 'rgba(0,0,0,0.25)',
                    }}>
                      ×{p.count}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Raw corrections */}
          <div className="card">
            <div className="card-title">Recent Corrections</div>
            {(!student.grammarHistory || student.grammarHistory.length === 0) ? (
              <div style={{ opacity: 0.5, padding: '12px 0' }}>No corrections yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                {student.grammarHistory.slice().reverse().slice(0, 20).map((g, i) => (
                  <div key={i} style={{
                    padding: '10px 14px', background: 'rgba(48,209,88,0.06)',
                    borderLeft: '3px solid #30d158', borderRadius: '0 8px 8px 0', fontSize: 13,
                  }}>
                    {g.feedback}
                    {g.timestamp && (
                      <span style={{ opacity: 0.4, fontSize: 11, marginLeft: 8 }}>
                        {fmtDate(g.timestamp)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {notifyOpen && (
        <NotifyModal
          student={student}
          onClose={() => setNotifyOpen(false)}
          onSent={() => {
            setNotifyOpen(false);
            setNotifySent({ to: student.displayName || student.email, ts: Date.now() });
            // auto-clear after 8s
            setTimeout(() => setNotifySent(null), 8000);
          }}
        />
      )}

      {tab === 'conversations' && (
        <div className="card">
          {convLoading ? (
            <div className="loading" style={{ minHeight: 200 }}><div className="loading-spinner" /></div>
          ) : conv?.privacyHidden ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FiLock /></div>
              <div className="empty-state-title">Conversations are private</div>
              <div className="empty-state-text">
                This student has enabled privacy mode. Progress metrics remain
                visible, but the conversation transcript is hidden out of respect
                for their privacy.
              </div>
            </div>
          ) : conv?.error ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FiAlertCircle /></div>
              <div className="empty-state-title">Could not load conversations</div>
              <div className="empty-state-text">{conv.error}</div>
            </div>
          ) : (conv?.messages || []).length === 0 ? (
            <div style={{ opacity: 0.5, padding: '20px 0', textAlign: 'center' }}>
              No conversation history yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {conv.messages.map((m, i) => {
                const isUser = (m.role === 'user' || m.role === 'student');
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '72%', padding: '10px 16px', borderRadius: 14,
                      borderBottomRightRadius: isUser ? 4 : 14,
                      borderBottomLeftRadius: isUser ? 14 : 4,
                      background: isUser ? 'rgba(41,151,255,0.15)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isUser ? 'rgba(41,151,255,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      fontSize: 14,
                    }}>
                      <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 2 }}>
                        {isUser ? student.displayName : 'Carlos'}
                      </div>
                      {m.text}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StudentDetail;
